'use client';

import 'swiper/css';
import 'swiper/css/navigation';

import { useGSAP } from '@gsap/react';
import { clsx } from 'clsx';
import { FC, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import Image from '@/components/Image';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';
import { CrowCardsDto } from './types';

interface Props {
	data: CrowCardsDto;
}

const CrowCards: FC<Props> = ({ data }) => {
	const { cards, title, subtitle } = data;
	const swiperRef = useRef<SwiperType | null>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const titleRef = useRef<HTMLHeadingElement>(null);
	const [showPagination, setShowPagination] = useState(false);

	const updatePaginationVisibility = (swiper: SwiperType) => {
		setShowPagination(!swiper.isLocked);
	};

	useGSAP(
		() => {
			const wrapper = wrapperRef.current;
			const titleEl = titleRef.current;
			const titleLines = titleEl?.querySelectorAll('p');
			const subtitleEl = wrapper?.querySelector(`.${css.subtitle}`);
			const sliderEl = wrapper?.querySelector(`.${css.sliderWrap}`);
			const slides = wrapper?.querySelectorAll(`.${css.crowSwiper} .swiper-slide`);

			if (!wrapper || !titleEl) return;

			const mm = gsap.matchMedia();

			mm.add(
				{
					isDesktop: '(min-width: 992px)',
					isMobile: '(max-width: 991px)',
					reduceMotion: '(prefers-reduced-motion: reduce)'
				},
				context => {
					const { isDesktop, reduceMotion } = context.conditions as gsap.Conditions;

					if (reduceMotion) return;

					const titleTargets = titleLines && titleLines.length ? Array.from(titleLines) : [titleEl];

					gsap.set(titleTargets, {
						opacity: 0,
						y: 40,
						filter: 'blur(10px)'
					});

					gsap.to(titleTargets, {
						opacity: 1,
						y: 0,
						filter: 'blur(0px)',
						duration: 1.1,
						ease: 'power3.out',
						stagger: titleTargets.length > 1 ? 0.15 : 0,
						scrollTrigger: {
							trigger: titleEl,
							start: 'top 80%',
							toggleActions: 'play none none reverse'
						}
					});

					if (subtitleEl) {
						gsap.set(subtitleEl, {
							opacity: 0,
							y: 40,
							filter: 'blur(10px)'
						});

						gsap.to(subtitleEl, {
							opacity: 1,
							y: 0,
							filter: 'blur(0px)',
							duration: 1,
							ease: 'power3.out',
							scrollTrigger: {
								trigger: subtitleEl,
								start: 'top 80%',
								toggleActions: 'play none none reverse'
							}
						});
					}

					if (sliderEl) {
						gsap.set(sliderEl, {
							autoAlpha: 0,
							y: 28
						});

						gsap.to(sliderEl, {
							autoAlpha: 1,
							y: 0,
							duration: 0.8,
							ease: 'power2.out',
							scrollTrigger: {
								trigger: sliderEl,
								start: 'top 85%',
								toggleActions: 'play none none reverse'
							}
						});

						if (slides?.length) {
							gsap.set(slides, {
								autoAlpha: 0,
								y: 36,
								scale: 0.96
							});

							gsap.to(slides, {
								autoAlpha: 1,
								y: 0,
								scale: 1,
								duration: 0.7,
								ease: 'power3.out',
								stagger: 0.08,
								scrollTrigger: {
									trigger: sliderEl,
									start: 'top 78%',
									toggleActions: 'play none none reverse'
								}
							});
						}
					}

					if (isDesktop)
						gsap.to(titleEl, {
							yPercent: -40,
							ease: 'none',
							scrollTrigger: {
								trigger: wrapper,
								start: 'top bottom',
								end: 'bottom top',
								scrub: true
							}
						});
				}
			);

			return () => {
				mm.revert();
			};
		},
		{
			scope: wrapperRef
		}
	);

	return (
		<section ref={wrapperRef} className={css.section}>
			<div className={css.container}>
				<div className={css.heading}>
					<Typography ref={titleRef} uppercase className={css.title} variant="h2" tag="h2">
						{title ? <StrapiRichContent content={title} /> : null}
					</Typography>

					{subtitle ? (
						<Typography className={css.subtitle} variant="body-l" tag="div">
							{subtitle ? <StrapiRichContent content={subtitle} /> : null}
						</Typography>
					) : null}
				</div>

				<div className={css.sliderWrap}>
					<Swiper
						watchOverflow
						speed={850}
						modules={[Pagination, A11y]}
						className={css.crowSwiper}
						spaceBetween={14}
						slidesPerView={1.1}
						initialSlide={0}
						pagination={{
							el: `.${css.pagination}`,
							clickable: true,
							dynamicBullets: true
						}}
						breakpoints={{
							640: { slidesPerView: 2.3 },
							992: { slidesPerView: 3.7 },
							1200: { slidesPerView: 5 }
						}}
						onSwiper={swiper => {
							swiperRef.current = swiper;
							updatePaginationVisibility(swiper);
							swiper.pagination?.render?.();
							swiper.pagination?.update?.();
						}}
						onResize={swiper => {
							updatePaginationVisibility(swiper);
							swiper.pagination?.update?.();
						}}
						onBreakpoint={swiper => {
							updatePaginationVisibility(swiper);
							swiper.pagination?.update?.();
						}}
						onUpdate={swiper => {
							updatePaginationVisibility(swiper);
							swiper.pagination?.update?.();
						}}
					>
						{cards.map(({ id, title, description, image }) => (
							<SwiperSlide key={id}>
								<div className={css.card}>
									{image ? (
										<Image
											preload
											height={78}
											width={78}
											src={image?.url ?? ''}
											alt="Statistic value image"
										/>
									) : null}

									<div className={css.cardFooter}>
										{title ? (
											<Typography uppercase className={css.cardTitle} tag="h3" variant="h6">
												<StrapiRichContent content={title} />
											</Typography>
										) : null}
										{description ? (
											<Typography className={css.cardDescription} tag="div" variant="body-m">
												<StrapiRichContent content={description} />
											</Typography>
										) : null}
									</div>
								</div>
							</SwiperSlide>
						))}
					</Swiper>

					<div className={clsx(css.pagination, !showPagination && css.paginationHidden)} />
				</div>
			</div>
		</section>
	);
};

export default CrowCards;
